<script lang="ts">
    import { _ } from "svelte-i18n"
    import Banner from "./Banner.svelte"
    import { cart } from "../../../../functionTools/cart"
    import { getCachedEntries } from "../../../../api"
    import SubNav from "./SubNav.svelte"
    import { overlay } from "../../../../store"
    import MediaQuery from "../../../widgets/MediaQuery.svelte"
    import Icon from "../../../widgets/Icon.svelte"
    import { mdiAccountCircle, mdiCart, mdiMagnify, mdiShoppingSearchOutline } from "@mdi/js"
    import { login } from "../../../../functionTools/login"
    import { initialLocale } from "../../../../i18n"
    import { spaLink } from "../../../../functionTools/navigation"
    import { preselectedDealerNumber } from "../../../../functionTools/iframeIntegration"

    const cartQty = $derived($cart?.reduce((total, item) => total + item.quantity, 0) || 0)
    let navigation: NavigationNode[] = $state([])

    async function loadNavigation() {
        const entries: NavigationTree[] = await getCachedEntries("navigation")
        const headerEntry = entries.find((e) => e.treeName === "header" && e.language === initialLocale)
        navigation = headerEntry?.nodes || []
    }

    loadNavigation()

    $effect(() => {
        if (mobileMenuOpen) document.body.style.overflow = "hidden"
        else document.body.style.overflow = "auto"
    })
    let mobileMenuOpen = $state(false)

    let scrollY = $state(0)
    let innerHeight = typeof window !== "undefined" ? window.innerHeight : 1080
    let lastY = 0
    let lastDirection = "up"
    let headerClass: "pin" | "unpin" = $state("pin")
    const SCROLL_THRESHOLD = 75 // Schwelle in Pixel, bevor Header wieder ausblendet

    function changeClass(scrollY: number): "pin" | "unpin" {
        // Wenn wir nahe am oberen Rand sind, immer einblenden
        if (scrollY < SCROLL_THRESHOLD) {
            lastY = scrollY
            lastDirection = "up"
            return "pin"
        }

        let result = headerClass
        const scrolledPxs = lastY - scrollY
        const scrollDirection = scrolledPxs < 0 ? "down" : scrolledPxs == 0 ? lastDirection : "up"
        const changedDirection = scrollDirection !== lastDirection
        if (changedDirection) {
            result = scrollDirection === "down" ? "unpin" : "pin"
            lastDirection = scrollDirection
        }
        lastY = scrollY
        return result
    }

    $effect(() => {
        innerHeight = typeof window !== "undefined" ? window.innerHeight : 1080
        void scrollY
        const shouldPin = innerHeight > 800
        const _headerClass = shouldPin ? "pin" : changeClass(scrollY)
        if (_headerClass !== headerClass) {
            headerClass = _headerClass
            const pListingHeader = document.querySelector("#product-listing-header")
            if (pListingHeader) {
                if (headerClass === "unpin") {
                    pListingHeader.classList.add("unpin")
                } else {
                    pListingHeader.classList.remove("unpin")
                }
            }
        }
    })
</script>

<svelte:window bind:scrollY />
{#snippet logo()}
    <div class="logo">
        {#if !$preselectedDealerNumber}
            <a
                href="/{initialLocale}"
                use:spaLink
                aria-label="Homepage"
                onclick={(e) => {
                    mobileMenuOpen = false
                    e.preventDefault()
                }}
            >
                <img src="../../../../../logo/renz-logo.png" alt="Renz Logo" />
            </a>
        {:else}
            <img src="../../../../../logo/renz-logo.png" alt="Renz Logo" />
        {/if}
    </div>
{/snippet}

{#snippet searchBtn(idx = 2)}
    <li class="search_mobile idx-{idx}">
        <a
            id="searchIconLink"
            aria-label="Search"
            role="button"
            tabindex="0"
            onclick={() => {
                $overlay = { show: true, type: "search", properties: {} }
            }}
            onkeydown={(e) => e.key === "Enter" && ($overlay = { show: true, type: "search", properties: {} })}
        >
            <Icon path={mdiMagnify} size="24px" />
        </a>
    </li>
{/snippet}

{#snippet secondaryNav(additionalClass = "", ariaLabel = "Secondary Navigation", searchIdx = 2)}
    <ul class="secondary-nav {additionalClass}" aria-label={ariaLabel}>
        {@render searchBtn(searchIdx)}
        <li class="cartbutton">
            <button
                class="button transparent"
                id="cartIconLink"
                onclick={() => ($overlay = { show: true, type: "cart", properties: {} })}
                onkeydown={(e) => e.key === "Enter" && ($overlay = { show: true, type: "cart", properties: {} })}
                tabindex="0"
                aria-label="Cart"
            >
                <Icon path={mdiCart} size="24px" />
                {#if $cart?.length}
                    <span class="num">{cartQty}</span>
                {/if}
            </button>
        </li>
        <li class="accountbutton">
            <button
                class="button tertiary sm"
                id="dealerLoginIconLink"
                aria-label="Login"
                class:loggedout={!$login}
                onclick={() => {
                    $overlay = { show: true, type: $login ? "logout" : "login", properties: {} }
                }}
            >
                <Icon path={mdiAccountCircle} size="24px" />
                <span class="text">{$login ? $_("header.logout") : $_("header.login")}</span>
            </button>
        </li>
    </ul>
{/snippet}
{#snippet contentSnippet()}
    <div class="nav-wrapper">
        <ul class="primary-nav left-nav">
            {#if !$preselectedDealerNumber}
                {#each navigation.filter((el) => el.headerPosition == "left") as navEl}
                    <SubNav
                        {navEl}
                        closeMenu={() => {
                            mobileMenuOpen = false
                        }}
                    />
                {/each}
            {/if}
        </ul>
        {@render logo()}
        <section class="primary-secondary-nav-wrapper">
            {#if !$preselectedDealerNumber}
                <ul class="primary-nav right-nav">
                    {#each navigation.filter((el) => el.headerPosition == "right") as navEl}
                        <SubNav
                            {navEl}
                            closeMenu={() => {
                                mobileMenuOpen = false
                            }}
                        />
                    {/each}
                    <SubNav
                        closeMenu={() => {
                            mobileMenuOpen = false
                        }}
                        navEl={{
                            name: $_("product-config.configurator"),
                            page: `${initialLocale}/brief-und-paketkaesten/konfigurator`,
                            nodes: [],
                            headerPosition: "right",
                            directUrl: true,
                            external: false,
                            externalUrl: null,
                            hash: null,
                        }}
                    />
                    <SubNav
                        closeMenu={() => {
                            mobileMenuOpen = false
                        }}
                        navEl={{
                            name: $_("header.my_orders"),
                            page: `${initialLocale}/order`,
                            nodes: [],
                            headerPosition: "right",
                            directUrl: true,
                            external: false,
                            externalUrl: null,
                            hash: null,
                        }}
                    />
                </ul>
                {@render secondaryNav()}
            {/if}
        </section>
    </div>
{/snippet}
{#snippet altSnippet()}
    <div class="nav-wrapper mobile">
        <ul class="secondary-nav left" aria-label="Secondary Navigation">
            <li>
                <button
                    class="button transparent"
                    id="pull"
                    class:active={mobileMenuOpen}
                    onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
                    onkeydown={(e) => e.key === "Enter" && (mobileMenuOpen = !mobileMenuOpen)}
                    tabindex="0"
                    aria-label="Menu"
                >
                    <span class="pull_button">
                        <span></span>
                        <span></span>
                        <span></span>
                    </span>
                    <span class="text">{$_("header.menu")}</span>
                </button>
            </li>
        </ul>
        <div class="primary-nav-wrapper" class:menuopen={mobileMenuOpen} data-testid="primary-nav">
            <ul class="primary-nav">
                {#each navigation as navEl}
                    {#key mobileMenuOpen}
                        <SubNav
                            {navEl}
                            closeMenu={() => {
                                mobileMenuOpen = false
                            }}
                        />
                    {/key}
                {/each}

                <SubNav
                    classList="secondary"
                    closeMenu={() => {
                        mobileMenuOpen = false
                    }}
                    navEl={{
                        name: $_("product-config.configurator"),
                        page: `${initialLocale}/brief-und-paketkaesten/konfigurator`,
                        nodes: [],
                        headerPosition: "right",
                        directUrl: true,
                        external: false,
                        externalUrl: null,
                        hash: null,
                    }}
                >
                    {#snippet iconSnippet()}
                        <svg
                            style="height: 24px; width: 24px;"
                            xmlns="http://www.w3.org/2000/svg"
                            width="49"
                            height="48"
                            viewBox="0 0 49 48"
                            fill="none"
                        >
                            <path
                                d="M16.5 28.384H18.27V22.384H16.5V24.5H13.5V26.27H16.5V28.384ZM20.27 26.27H35.5V24.5H20.27V26.27ZM30.73 21.616H32.5V19.5H35.5V17.73H32.5V15.618H30.73V21.616ZM13.5 19.5H28.732V17.73H13.5V19.5ZM18.5 40V36H9.732C8.81067 36 8.042 35.692 7.426 35.076C6.81 34.46 6.50133 33.6907 6.5 32.768V11.232C6.5 10.3107 6.80867 9.542 7.426 8.926C8.04333 8.31 8.81133 8.00133 9.73 8H39.27C40.19 8 40.958 8.30867 41.574 8.926C42.19 9.54333 42.4987 10.312 42.5 11.232V32.77C42.5 33.69 42.1913 34.4587 41.574 35.076C40.9567 35.6933 40.1887 36.0013 39.27 36H30.5V40H18.5ZM9.732 34H39.27C39.5767 34 39.8587 33.872 40.116 33.616C40.3733 33.36 40.5013 33.0773 40.5 32.768V11.232C40.5 10.924 40.372 10.6413 40.116 10.384C39.86 10.1267 39.578 9.99867 39.27 10H9.73C9.42333 10 9.14133 10.128 8.884 10.384C8.62667 10.64 8.49867 10.9227 8.5 11.232V32.77C8.5 33.0767 8.628 33.3587 8.884 33.616C9.14 33.8733 9.422 34.0013 9.73 34"
                                fill="white"
                            ></path>
                        </svg>
                    {/snippet}
                </SubNav>

                <SubNav
                    classList="secondary"
                    closeMenu={() => {
                        mobileMenuOpen = false
                    }}
                    navEl={{
                        name: $_("header.my_orders"),
                        page: `${initialLocale}/order`,
                        nodes: [],
                        headerPosition: "right",
                        directUrl: true,
                        external: false,
                        externalUrl: null,
                        hash: null,
                    }}
                >
                    {#snippet iconSnippet()}
                        <Icon path={mdiShoppingSearchOutline} size="24px" color="white" />
                    {/snippet}
                </SubNav>
            </ul>
        </div>
        {@render logo()}
        {@render secondaryNav("right", "Secondary Navigation Right", 1)}
    </div>
{/snippet}
<header id="header" class={headerClass}>
    <a
        href="#main-content"
        class="skip-link"
        onclick={(e) => {
            e.preventDefault()
            window.scrollBy({ top: 40, behavior: "smooth" })
            document.getElementById("main-content")?.focus()
        }}>Navigation überspringen</a
    >
    {#if !$preselectedDealerNumber}
        <section aria-label="Banner">
            <Banner />
        </section>
    {/if}
    <nav class="main-header" aria-label="Main Navigation">
        {#if typeof window == "undefined"}
            {@render altSnippet()}
        {:else}
            <MediaQuery query={"(min-width: 1600px)"} {contentSnippet} {altSnippet} />
        {/if}
    </nav>
</header>

<style lang="less" global>
    @import "../../../../css/variables.less";
    #product-listing-header {
        header {
            transition: top 0.3s ease-in-out;
        }

        .filter-wrapper {
            transition:
                top 0.3s ease-in-out,
                height 0.3s ease-in-out;
        }

        &.unpin {
            header {
                top: 0px !important;
            }
            .filter-wrapper {
                top: calc(45px);
                height: min(100%, calc(100vh - 45px));
                max-height: calc(100vh - 45px);
            }
        }
    }
    #header {
        position: sticky;
        top: 0px;
        z-index: 1000;
        width: 100%;
        box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.25);
        &.unpin {
            transform: translateY(-100%);
            transition: transform 0.3s ease-in-out;
        }
        a {
            transition: color 200ms ease-in-out;
            cursor: pointer;
            &:hover {
                color: var(--red-200);
            }
        }
        .search_mobile {
            &.idx-1 {
                display: none;
                @media @header_mobile {
                    display: flex;
                }
            }
            height: 100%;
            display: flex;
            align-items: center;
            a {
                height: 100%;
            }
            @media @header_mobile {
                &.idx-2 {
                    display: none;
                }
            }
        }

        .secondary-nav {
            display: flex;
            gap: 1.2rem;
            align-items: center;
            z-index: 1;
            * {
                color: var(--grey-400);
            }
            a {
                cursor: pointer;
                display: flex;
                align-items: center;
            }
            @media @max-mobile {
                gap: 0.6rem;
                &.left {
                    justify-content: flex-start;
                }
                &.right {
                    justify-content: flex-end;
                }
            }
            .cartbutton {
                position: relative;
                display: flex;
                align-items: center;
                button {
                    overflow: visible;
                }
                .num {
                    border-radius: 120px;
                    background: var(--red-100);
                    width: 18px;
                    height: 18px;
                    position: absolute;
                    right: -8px;
                    top: -8px;
                    padding: 2px 6px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    gap: 10px;
                    font-size: 16px;
                    line-height: 15px;
                    color: var(--white-100);
                }
            }
            .accountbutton {
                height: 30px;
                button {
                    .text {
                        display: none;
                        @media (min-width: 450px) {
                            display: inline;
                        }
                    }
                }
            }
        }
        button#pull {
            .flex();
            flex-direction: row;
            justify-content: center;
            align-items: center;
            text-align: center;
            justify-content: center;
            cursor: pointer;
            z-index: 500;
            text-align: left;
            font-size: 18px;
            color: inherit;
            .text {
                font-size: 1rem;
                padding-left: 7px;
            }

            .pull_button {
                display: block;
                width: 20px;
                span {
                    .transition();
                    display: block;
                    width: 100%;
                    height: 2px;
                    background: var(--surface-3);
                    margin-bottom: 3px;
                    border-radius: 100px;
                    &:nth-of-type(3) {
                        margin-bottom: 0px;
                    }
                }
            }
            &:hover {
                text-decoration: none;
            }
            &.active {
                .pull_button {
                    span:nth-child(1) {
                        transform: rotate(-45deg);
                        margin-top: -2px;
                    }
                    span:nth-child(2) {
                        .opacity(0);
                        visibility: hidden;
                    }
                    span:nth-child(3) {
                        transform: rotate(45deg);
                        margin-top: -10px;
                    }
                }
            }
            display: none;
            @media @header_mobile {
                display: flex;
            }
        }
        nav {
            position: relative;
            width: 100%;
            height: 72px;
            background-color: var(--white-100);
            display: flex;
            justify-content: center;
            padding: 0px var(--default-horizontal-padding);

            .nav-wrapper {
                display: flex;
                padding: 0.6rem 0px;
                height: 100%;
                gap: 2.4rem;
                max-width: var(--max-container-width);
                @media @max-mobile {
                    gap: 0.6rem;
                    justify-content: space-between;
                    align-items: center;
                }
                width: 100%;
                justify-content: space-between;
                .logo {
                    flex-grow: 1;
                    position: absolute;
                    left: 50%;
                    transform: translateX(-50%);
                    height: 45px;
                    z-index: 0;
                    a {
                        height: 100%;
                        display: flex;
                        align-items: center;
                        object-fit: contain;
                        img {
                            height: 100%;
                            width: 100%;
                            object-fit: contain;
                        }
                    }
                    @media @max-mobile {
                        height: 40px;
                        a img {
                            max-width: 120px;
                        }
                    }
                }
                .primary-secondary-nav-wrapper {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 2.4rem;

                    @media @max-mobile {
                        gap: 1.2rem;
                        width: 100%;
                        max-width: 100%;
                    }
                }
                .primary-nav {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1.2rem;
                }
            }
        }
        @media @header_mobile {
            .primary-nav-wrapper {
                position: absolute;
                left: -100vw;
                width: 100%;
                top: 100%;
                height: calc(100vh - 100%);
                transition: left 0.5s ease;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                &::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    z-index: -1;
                }
                .primary-nav {
                    position: relative;
                    max-width: var(--max-container-width);
                    display: flex;
                    align-items: flex-start !important;
                    justify-content: flex-start !important;
                    flex-direction: column;
                    width: 100%;
                    max-height: 100%;
                    gap: 0px !important;
                    background-color: var(--white-100);
                    overflow: auto;
                    border-top: 1px solid var(--grey-300);

                    &:has(.mobileSubnavOpen),
                    &:has(.showMobileSubnav) {
                        overflow: visible;
                        background-color: transparent;
                    }

                    &:has(.mobileSubnavOpen) > :global(li:not(:has(.mobileSubnavOpen))),
                    &:has(.showMobileSubnav) > :global(li:not(:has(.showMobileSubnav))) {
                        display: none;
                    }

                    .secondary {
                        width: 100%;
                        height: 48px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        background-color: var(--grey-400);
                        .parent-nav {
                            background-color: transparent;
                            border-bottom: 0;
                        }
                        span {
                            color: var(--white-100) !important;
                        }
                    }
                }

                &.menuopen {
                    left: 0px;
                }
            }

            :global .parent-nav {
                width: 100%;
                height: 48px;
                background-color: var(--white-100);
                border-bottom: 1px solid var(--grey-300);
                padding: 10px 20px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                * {
                    color: var(--grey-400) !important;
                    font-size: var(--bodyfontsize) !important;
                }
            }
        }
    }
</style>
